import React, { useState, useContext } from "react";
import { i18n } from "../../translate/i18n";
import { 
    Avatar, 
    CardHeader, 
    Grid,
    Dialog,
    DialogContent,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { AuthContext } from "../../context/Auth/AuthContext";
import { createInfoChatTicketInfoStyles } from "../infoChatTicketInfoStyles";

// Estilos do cabeçalho de informações do ticket (M5.4b) — exclusivamente o
// anel de foco visível do título clicável (Typography) e do avatar
// clicável. Usa `:focus-visible` (não `Mui-focusVisible`) porque nenhum
// dos dois é `ButtonBase` do MUI — o foco/teclado é implementado
// manualmente via `role`/`tabIndex`/`onKeyDown` diretamente nas props
// (`titleTypographyProps` e o `Avatar` espalham props desconhecidas
// no elemento raiz real), então o equivalente nativo do navegador para "só
// aparece em navegação por teclado" é `:focus-visible`, nunca `:focus`
// sozinho (que também dispara em clique de mouse). O `CardHeader` raiz
// propositalmente NÃO recebe nenhuma semântica interativa (sem
// `role`/`tabIndex`/`onKeyDown`) — título e avatar são controles irmãos no
// DOM, nunca um aninhado dentro do outro.
const useStyles = makeStyles((theme) => {
    const infoChatTicketInfo = createInfoChatTicketInfoStyles(theme);

    return {
        // Estilos para o modal da imagem
        imageModal: {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
        },
        imageModalContent: {
            maxWidth: "90vw",
            maxHeight: "90vh",
        },
        expandedImage: {
            width: "100%",
            height: "auto",
            maxWidth: "500px",
            borderRadius: theme.spacing(1),
        },
        clickableAvatar: {
            cursor: "pointer",
            "&:hover": {
                opacity: 0.8,
            },
        },
        // Título (Typography dentro do CardHeader) — alternativa de teclado
        // para abrir as informações do contato (Enter/Space chamam o
        // `onClick` recebido do pai, mesmo caminho do clique de mouse). O
        // `CardHeader` raiz permanece sem semântica interativa própria —
        // só o título e o avatar (irmãos no DOM) são focáveis, evitando
        // controle interativo aninhado dentro de outro controle
        // interativo.
        focusableTitle: {
            outline: "none",
            "&:focus-visible": {
                outline: infoChatTicketInfo.focusRingAccent.outline,
                outlineOffset: infoChatTicketInfo.focusRingAccent.outlineOffset
            }
        },
        // Avatar — clicável por teclado só quando há foto (aplicado
        // condicionalmente no JSX); ação distinta do título/cabeçalho (abre
        // o modal de imagem), nunca o `onClick` do cabeçalho.
        focusableAvatar: {
            outline: "none",
            "&:focus-visible": {
                outline: infoChatTicketInfo.focusRingAccent.outline,
                outlineOffset: infoChatTicketInfo.focusRingAccent.outlineOffset
            }
        }
    };
});

const TicketInfo = ({ contact, ticket, onClick }) => {
    const classes = useStyles();
    const [amount, setAmount] = useState("");
    const { user } = useContext(AuthContext);
    const [imageModalOpen, setImageModalOpen] = useState(false); // Estado para o modal da imagem

    // Função para abrir modal da imagem
    const handleImageClick = (e) => {
        e.stopPropagation(); // Prevenir que o clique no avatar execute outros handlers
        if (contact?.urlPicture) {
            setImageModalOpen(true);
        }
    };

    // Função para fechar modal da imagem
    const handleImageModalClose = () => {
        setImageModalOpen(false);
    };

    // Ativação do título por teclado (Enter/Space) — mesmo caminho do
    // `onClick` recebido do pai, chamado via clique de mouse. O título é
    // um controle irmão do avatar (nenhum dos dois é ancestral do outro no
    // DOM do CardHeader), então não há bubbling entre eles — chama
    // `onClick` exatamente uma vez, sem checagem de alvo necessária.
    const handleTitleKeyDown = (e) => {
        if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
            e.preventDefault();
            if (onClick) {
                onClick(e);
            }
        }
    };

    // Ativação do avatar por teclado (Enter/Space) — reaproveita
    // `handleImageClick` integralmente (mesma condição `contact?.urlPicture`,
    // mesmo `stopPropagation`), só trocando a origem do evento. O
    // `stopPropagation` dentro de `handleImageClick` impede que este keydown
    // suba até o cabeçalho e dispare o `onClick` dele também.
    const handleAvatarKeyDown = (e) => {
        if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
            e.preventDefault();
            handleImageClick(e);
        }
    };

    const avatarIsInteractive = Boolean(contact?.urlPicture);

    const renderCardReader = () => {
        return (
            <CardHeader
                onClick={onClick}
                style={{ cursor: "pointer" }}
                titleTypographyProps={{
                    noWrap: true,
                    role: "button",
                    tabIndex: 0,
                    "aria-label": "Abrir informações do contato",
                    onKeyDown: handleTitleKeyDown,
                    className: classes.focusableTitle
                }}
                subheaderTypographyProps={{ noWrap: true }}
                avatar={
                    <Avatar
                        src={contact?.urlPicture}
                        alt="contact_image"
                        className={
                            avatarIsInteractive
                                ? `${classes.clickableAvatar} ${classes.focusableAvatar}`
                                : classes.clickableAvatar
                        }
                        onClick={handleImageClick}
                        {...(avatarIsInteractive
                            ? {
                                role: "button",
                                tabIndex: 0,
                                "aria-label": "Ampliar foto do contato",
                                onKeyDown: handleAvatarKeyDown
                            }
                            : {})}
                    />
                }
                title={`${(contact?.name && contact.name.length > 12) ?
                    `${contact.name.substring(0, 12)}...` :
                    contact?.name || '(sem contato)'} #${ticket?.id}`}
                subheader={[
                    ticket?.user && `${i18n.t("messagesList.header.assignedTo")} ${ticket?.user?.name}`,
                    contact?.contactWallets && contact.contactWallets.length > 0
                        ? `• ${i18n.t("wallets.wallet")}: ${contact.contactWallets[0].wallet?.name || 'N/A'}`
                        : null
                ].filter(Boolean).join(' ')}
            />
        );
    }

    const handleChange = (event) => {
        const value = event.target.value;
        setAmount(value);
    }

    return (
        <React.Fragment>
            <Grid container alignItems="center" spacing={10}>
                {/* Conteúdo do contato à esquerda */}
                <Grid item xs={6}>
                    {renderCardReader()}
                </Grid>
            </Grid>

            {/* Modal da Imagem */}
            <Dialog
                open={imageModalOpen}
                onClose={handleImageModalClose}
                className={classes.imageModal}
                maxWidth="md"
                fullWidth
            >
                <DialogContent className={classes.imageModalContent}>
                    <img 
                        src={contact?.urlPicture} 
                        alt={contact?.name || "Foto do contato"}
                        className={classes.expandedImage}
                    />
                </DialogContent>
            </Dialog>
        </React.Fragment>
    );
};

export default TicketInfo;